import { InformationCircleIcon } from '@heroicons/react/24/solid'
import cn from 'classnames'
import {
  forwardRef,
  useState,
  useEffect,
  type ReactNode,
  type ChangeEvent,
  type FocusEvent,
  type ForwardedRef,
  type InputHTMLAttributes,
} from 'react'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement>, DefaultProps {
  label?: string
  error?: string
  icon?: ReactNode
  iconsClassName?: string
  inputClassName?: string
  valueFormatter?: (_value: string) => string
  isPII?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      className,
      iconsClassName,
      inputClassName,
      testId,
      id,
      ariaLabel,
      type,
      disabled,
      icon,
      isPII,
      valueFormatter,
      ...props
    },
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [hasValue, setHasValue] = useState(Boolean(props.value))

    useEffect(() => {
      setHasValue(Boolean(props.value))
    }, [props.value])

    const inputClasses = twMerge(
      cn(
        'border px-sm rounded w-full disabled:text-gray-400 disabled:bg-gray-50 box-border',
        'typo-primary-base text-text-header',
        {
          'pt-[22px] pb-[12px]': Boolean(label),
          'py-[17px]': !label,
          'outline-none focus:outline-2 focus:outline focus:-outline-offset-2 focus:outline-borders-focus hover:border-gray-400 disabled:border-gray-300':
            !error,
          'border-red-700 pr-9 outline-none focus:ring-red-500 bg-red-50 disabled:border-gray-300': Boolean(error),
          'cursor-not-allowed touch-none': Boolean(disabled),
          'fs-exclude': isPII,
        },
      ),
      inputClassName,
    )

    const labelClasses = cn(
      'pointer-events-none absolute ml-[17px] transition-all duration-200 ease-in-out origin-top-left typo-primary-base',
      {
        'translate-y-[18px]': !isFocused && !hasValue && !props.placeholder,
        'typo-primary-xs-semibold translate-y-[12px]': isFocused || hasValue || props.placeholder,
        'opacity-70': disabled,
        'text-text-meta': !isFocused,
        'text-text-body': isFocused,
      },
    )

    return (
      <div className={className} data-testid="input-wrapper">
        <div className="relative">
          {label ? <span className={labelClasses}>{label}</span> : null}
          <input
            {...(testId ? { 'data-testid': testId } : {})}
            {...(id ? { id } : {})}
            {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
            className={inputClasses}
            ref={ref}
            disabled={disabled}
            type={type}
            {...props}
            onFocus={(event: FocusEvent<HTMLInputElement>) => {
              setIsFocused(true)
              props.onFocus && props.onFocus(event)
            }}
            onBlur={(event: FocusEvent<HTMLInputElement>) => {
              setIsFocused(false)
              props.onBlur && props.onBlur(event)
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setHasValue(Boolean(event.target.value))
              props.onChange && props.onChange(event)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                event.stopPropagation()
                props.onKeyDown && props.onKeyDown(event)
              }
            }}
            onInput={(event) => {
              if (valueFormatter) {
                event.currentTarget.value = valueFormatter(event.currentTarget.value)
              }
              if (props.onInput) {
                props.onInput(event)
              }
            }}
          />
          {error ? (
            <span
              className={cn('absolute right-3 top-1/2 -translate-y-1/2 transform pointer-events-none', {
                'right-3': !icon,
                'right-9': Boolean(icon),
              })}
            >
              <InformationCircleIcon className="w-5 h-5 fill-red-400" />
            </span>
          ) : null}
          {icon ? (
            <span
              className={twMerge(
                cn('absolute right-3 top-1/2 -translate-y-1/2 transform pointer-events-none'),
                iconsClassName,
              )}
            >
              {icon}
            </span>
          ) : null}
        </div>
        {Boolean(error) && <p className="text-text-error typo-primary-sm-semibold mt-3xs">{error}</p>}
      </div>
    )
  },
)

Input.displayName = 'Input'

export { Input }
